import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { getDynamicUrl } from '../../middleware/lokaliseUrlMiddleware';
import { AtExchangeAsGuestModal, PaymentProvider } from '@homeexchange/payment';

import {
    addAvailability as addAvailabilityAction,
    fetchAvailabilities as fetchAvailabilitiesAction
} from '../../actions/availabilities';

import i18n from '../../i18n';

import Availability from '../../models/Availability';
import Exchange from '../../models/Exchange';
import User from '../../models/User';
import Conversation from '../../models/Conversation';

import Utils from '../../utils/utils';

export class PaymentSuccessPopup extends React.PureComponent {
    static propTypes = {
        exchange: PropTypes.instanceOf(Exchange).isRequired,
        user: PropTypes.instanceOf(User),
        show: PropTypes.bool,
        onPopupConfirm: PropTypes.func.isRequired,
        addAvailability: PropTypes.func.isRequired,
        fetchAvailabilities: PropTypes.func.isRequired,
        conversation: PropTypes.instanceOf(Conversation),
        localeDescription: PropTypes.string
    };

    static defaultProps = {
        show: false
    };

    constructor(props) {
        super(props);

        this.state = {
            ready: false,
            hasOverlapingAvailabilities: false
        };

        this.addOverlappinAvalabilite = this.addOverlappinAvalabilite.bind(this);
        this.addToggleSuccess = this.addToggleSuccess.bind(this);
    }

    componentDidMount() {
        if (this.props.show) {
            this.checkAvailabilities();
        }
    }

    componentDidUpdate(prevProps) {
        if (!prevProps.show && this.props.show) {
            this.checkAvailabilities();
        }
    }

    checkAvailabilities() {
        const { exchange, user, fetchAvailabilities } = this.props;
        const exchangeAsHost = this.props.conversation.getExchangeAsHost();

        if (!exchange) {
            return;
        }

        this.setState({
            ready: false
        });

        // considering only published homes with no available period at the same period
        const homes = user.get('homes').filter((home) => home.isPublished());
        const availabilitiesPromises = [];

        homes.forEach((home) => {
            availabilitiesPromises.push(fetchAvailabilities(home.id));
        });

        Promise.all(availabilitiesPromises).then((availabilities) => {
            let hasOverlapingAvailabilities = false;

            // For each homes, check if the home already has guestwanted period in the calendar
            availabilities.forEach((homeAvailabilities) => {
                homeAvailabilities = homeAvailabilities.data.map(
                    (availability) => new Availability(availability)
                );
                // Only keep overlaping GuestWanted periods
                const period = moment.range(exchange.get('start_on'), exchange.get('end_on'));
                homeAvailabilities = homeAvailabilities.filter(
                    (availability) =>
                        (availability.get('type') === Availability.NON_RECIPROCAL.type ||
                            availability.get('type') === Availability.AVAILABLE.type) &&
                        availability.range().overlaps(period)
                );
                if (homeAvailabilities.length > 0) {
                    hasOverlapingAvailabilities = true;
                }
                // Don't display the toggle to a reciprocal exchange
                if (
                    Availability.RECIPROCAL &&
                    exchangeAsHost &&
                    exchange.get('start_on') === exchangeAsHost.get('start_on') &&
                    exchange.get('end_on') === exchangeAsHost.get('end_on')
                ) {
                    hasOverlapingAvailabilities = true;
                }
            });

            this.setState({
                ready: true,
                hasOverlapingAvailabilities
            });
        });
    }

    // Called by the modal when the user closes it with the calendar consent switch on
    addOverlappinAvalabilite() {
        const { addAvailability, exchange, user } = this.props;
        const homes = user.get('homes').filter((home) => home.isPublished());

        // create available period on each guest's home calendar
        homes.forEach((home) =>
            addAvailability(home.id, {
                start_on: exchange.get('start_on'),
                end_on: exchange.get('end_on'),
                type: Availability.NON_RECIPROCAL.type
            })
        );
    }

    // Called by the modal whenever it is closed, whatever the consent
    addToggleSuccess() {
        // Show Hotjar survey
        if (window.hj && typeof hj !== undefined) {
            const locale = i18n.language || 'en';
            hj('trigger', `exchange-finalization-paid-${locale}`);
        }

        this.props.onPopupConfirm();
    }

    getExchangeView() {
        const { exchange } = this.props;
        const home = exchange.get('home');

        return {
            city: home.firstAdmin(),
            country: home.country(),
            nbDay: moment(exchange.get('start_on')).diff(moment(), 'days'),
            dateStart: moment(exchange.get('start_on')).format('L'),
            dateEnd: moment(exchange.get('end_on')).format('L')
        };
    }

    render() {
        const { show, user, localeDescription } = this.props;
        const { ready, hasOverlapingAvailabilities } = this.state;

        // the modal has no loading state: wait for the availabilities check so that
        // the calendar consent switch is only offered when it is relevant
        if (!show || !ready) {
            return null;
        }

        return (
            <PaymentProvider
                config={{
                    apiUrl: getDynamicUrl(process.env.URL_BFF),
                    assetsUrl: Utils.getAssetsDomain(),
                    locale: localeDescription
                }}
            >
                <AtExchangeAsGuestModal
                    isOpen={show}
                    exchange={this.getExchangeView()}
                    variant={user && user.isCollection() ? 'collection' : 'regular'}
                    isCalendarConsentAvailable={!hasOverlapingAvailabilities}
                    addOverlappinAvalabilite={this.addOverlappinAvalabilite}
                    addToggleSuccess={this.addToggleSuccess}
                />
            </PaymentProvider>
        );
    }
}

const mapStateToProps = (state) => ({
    availabilities: state.availabilities,
    localeDescription: state.localeDescription
});

const mapDispatchToProps = (dispatch) => ({
    addAvailability: bindActionCreators(addAvailabilityAction, dispatch),
    fetchAvailabilities: bindActionCreators(fetchAvailabilitiesAction, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(PaymentSuccessPopup);
